/**
 * Conversation Area Component
 *
 * Visual chat interface for displaying episode testing conversations.
 * CRITICAL: Parses and unwraps XML-formatted voice responses for natural display.
 * Users should NEVER see raw XML tags like <narrator>Hello</narrator>.
 *
 * Story: 11.7 - Episode Evaluation & Testing System
 * Task: 8 - Frontend Conversation Area Component
 */

import { useEffect, useRef } from 'react';
import type { ConversationMessage } from '../services/testingApi';

// ============================================================================
// Type Definitions
// ============================================================================

interface VoiceSegment {
  voiceName: string; // "narrator", "maria", etc.
  content: string;
}

interface ConversationAreaProps {
  messages: ConversationMessage[];
  isLoading?: boolean;
}

// ============================================================================
// Response Parsing Utility
// ============================================================================

/**
 * Extract plain text from user message content.
 * Handles both new JSON format and legacy plain text format.
 *
 * Story 3.9.18: Unified Conversation JSON Format
 * New format: [{"persona": "User", "message": "Hello"}]
 * Legacy format: "Hello" (plain text)
 *
 * @param content - Content string (JSON or plain text)
 * @returns Plain text message
 */
function extractUserMessageText(content: string): string {
  if (!content) return '';

  // Try to parse as JSON array (new unified format)
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const firstItem = parsed[0] as { persona?: string; message?: string };
      if (firstItem.message !== undefined) {
        return firstItem.message;
      }
    }
  } catch {
    // Not valid JSON - treat as plain text (legacy format)
  }

  // Return as-is (legacy plain text format)
  return content;
}

/**
 * Parse system response into voice segments
 *
 * Accepts JSON string with turns array (direct array format):
 * [{"persona": "Narrator", "message": "..."}, {"persona": "Vendor", "message": "..."}]
 *
 * Also handles JSON wrapped in markdown code blocks:
 * ```json\n[{...}]\n```  or  ```\n[{...}]\n```
 *
 * CRITICAL: Parses structured JSON and merges consecutive turns from the same persona
 * to avoid visual confusion where multiple turns from one persona look like separate messages.
 */
function parseVoiceResponse(content: string): VoiceSegment[] {
  const segments: VoiceSegment[] = [];

  try {
    // Step 1: Extract JSON from possible markdown wrapper
    let jsonStr = content.trim();

    // Check for markdown code block (```json ... ``` or ``` ... ```)
    const markdownMatch = content.match(/```(?:json)?\s*\n(.*?)\n```/s);
    if (markdownMatch) {
      jsonStr = markdownMatch[1].trim();
    }

    // Step 2: Parse JSON (direct array format)
    const turns = JSON.parse(jsonStr);

    // Step 3: Validate and build segments
    if (Array.isArray(turns)) {
      for (const turn of turns) {
        if (turn.persona && turn.message) {
          const lastSegment = segments[segments.length - 1];

          // Merge consecutive turns from the same persona
          if (lastSegment && lastSegment.voiceName.toLowerCase() === turn.persona.toLowerCase()) {
            // Append to existing segment with line break
            lastSegment.content += '\n\n' + turn.message;
          } else {
            // Create new segment
            segments.push({
              voiceName: turn.persona,
              content: turn.message,
            });
          }
        }
      }
    }
  } catch (error) {
    console.error('Failed to parse voice response:', error, content);

    // Fallback to plain text display
    if (content.trim()) {
      segments.push({
        voiceName: 'system',
        content: content.trim(),
      });
    }
  }

  return segments;
}

// ============================================================================
// Component
// ============================================================================

export function ConversationArea({ messages, isLoading = false }: ConversationAreaProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="bg-white border border-gray-300 rounded-lg overflow-hidden flex flex-col h-full">
      {/* Messages Area */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        role="log"
        aria-live="polite"
        aria-label="Conversation messages"
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 text-center">
              No messages yet. Start the conversation by sending a message or running a simulation.
            </p>
          </div>
        ) : (
          messages.map((message, index) => {
            if (message.role === 'user') {
              return (
                <UserMessage key={index} content={message.content} aria-label="User message" />
              );
            } else {
              return (
                <SystemMessage key={index} content={message.content} aria-label="System message" />
              );
            }
          })
        )}

        {/* Loading Indicator */}
        {isLoading && (
          <div
            className="flex items-center space-x-2 text-gray-600"
            aria-busy="true"
            aria-live="polite"
          >
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <span className="text-sm">Generating response...</span>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

// ============================================================================
// User Message Component
// ============================================================================

interface UserMessageProps {
  content: string;
  'aria-label'?: string;
}

function UserMessage({ content, 'aria-label': ariaLabel }: UserMessageProps) {
  // Extract plain text from JSON format (Story 3.9.18)
  const displayContent = extractUserMessageText(content);

  return (
    <div className="flex justify-end" aria-label={ariaLabel}>
      <div className="max-w-[70%] bg-blue-600 text-white rounded-lg px-4 py-3">
        <div className="text-xs font-semibold mb-1 text-blue-200">User</div>
        <div className="text-sm whitespace-pre-wrap break-words">{displayContent}</div>
      </div>
    </div>
  );
}

// ============================================================================
// System Message Component (with XML Parsing)
// ============================================================================

interface SystemMessageProps {
  content: string;
  'aria-label'?: string;
}

function SystemMessage({ content, 'aria-label': ariaLabel }: SystemMessageProps) {
  const voiceSegments = parseVoiceResponse(content);

  return (
    <>
      {voiceSegments.map((segment, index) => {
        const isNarrator = segment.voiceName.toLowerCase() === 'narrator';

        return (
          <div key={index} className="flex justify-start" aria-label={ariaLabel}>
            <div
              className={`max-w-[70%] rounded-lg px-4 py-3 ${
                isNarrator ? 'bg-blue-50' : 'bg-gray-200 text-gray-900'
              }`}
            >
              <VoiceSegment voiceName={segment.voiceName} content={segment.content} />
            </div>
          </div>
        );
      })}
    </>
  );
}

// ============================================================================
// Voice Segment Component
// ============================================================================

interface VoiceSegmentProps {
  voiceName: string;
  content: string;
}

function VoiceSegment({ voiceName, content }: VoiceSegmentProps) {
  // Normalize voice name for comparison (case-insensitive)
  const normalizedName = voiceName.toLowerCase();
  const isNarrator = normalizedName === 'narrator';

  // Capitalize first letter of voice name for display
  const displayName = voiceName.charAt(0).toUpperCase() + voiceName.slice(1);

  // Different styling for Narrator vs other personas
  if (isNarrator) {
    return (
      <div aria-label="Narrator">
        <div className="text-xs font-semibold mb-2 text-blue-700 flex items-center gap-1">
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
          {displayName}
        </div>
        <div className="text-sm whitespace-pre-wrap break-words text-blue-900 italic border-l-4 border-blue-400 pl-3">
          {content}
        </div>
      </div>
    );
  }

  // Regular persona styling
  return (
    <div aria-label={`${displayName} speaking`}>
      <div className="text-xs font-semibold mb-2 text-green-700">{displayName}</div>
      <div className="text-sm whitespace-pre-wrap break-words">{content}</div>
    </div>
  );
}
